<script lang="ts" setup>
import { computed } from 'vue'

import { Figure, Figcaption, Picture, PictureWithBlur, Image, BlockContainer } from '@toni/brand-ui'

import { usePageStore } from '../../stores/page'
import { useConfigStore } from '../../stores/config'
import { useContentBlock } from '../../content-blocks/composables/use-content-block'
import { createImgSrc, createImgSrcSetArray, getImgDefinitions, setMediaQuery, isPortraitImage } from '../bauer-image/image-utils'
import { toValidHTML } from '../../utils/html-utils'
import { usePageBlockWidth } from '../../composables/use-page-block-width.ts'

import type { ContentBlocksAndAdSlotMarkers } from '../../content-blocks/types/content-block-types'

const props = defineProps<{
  id: string
}>()

const id = computed(() => props.id)
const contentBlock = useContentBlock(id.value, 'image')

const pageStore = usePageStore()

const brand = usePageStore().brandFromStore as string
const imageServiceUrl = useConfigStore().rsConfig.imageServiceUrl as string
const imageSrcDefinitions = useConfigStore().rsConfig.imageSrcDefinitions
// Get setName for portrait images from imageSrcDefinitions in constants
const setName = `image-${contentBlock.value.data.image?.layout?.size}` as string
const imageDefinitions = getImgDefinitions(brand, imageSrcDefinitions, setName)
const imgFallbackWidth = imageDefinitions.maxWidth ?? imageSrcDefinitions?.maxWidth ?? 0

const image = computed(() => contentBlock.value.data.image)

const isPortrait = computed(() => {
  if (!image.value) return false
  return isPortraitImage(image.value.width || 0, image.value.height || 0)
})

const hasImageSizeL = contentBlock.value.data.image.layout?.size === 'l'
const isHome = computed(() => pageStore.isHome as boolean)
const pageBlockWidthSize = computed(() => usePageBlockWidth(pageStore.pageData?.pageMeta, isHome.value, brand).value)
const size = computed(() => {
  if (hasImageSizeL) {
    return 'full'
  } else {
    return pageBlockWidthSize.value
  }
})

const imgSrc = () => {
  if (!image.value?.imageId && !image.value?.externalImageSrc) return undefined

  return (createImgSrc({
    image: image.value,
    brand,
    imageServiceUrl,
    setName,
    srcSetDefinition: {
      format: 'landscape'
    }
  }, imgFallbackWidth))?.href ?? ''
}

const imgSrcSetArray = (image: ContentBlocksAndAdSlotMarkers) => {
  return createImgSrcSetArray({
    image,
    brand,
    imageServiceUrl,
    setName,
    imageSrcDefinitions
  })
}

const blurredBgSrc = () => {
  if (!image.value?.imageId) return undefined
  return (createImgSrc({
    image: image.value,
    brand,
    imageServiceUrl,
    setName: 'image-m',
    srcSetDefinition: {
      format: 'landscape'
    }
  }, imgFallbackWidth))?.href ?? ''
}
</script>

<template>
  <BlockContainer :size="size"
                  :class="{ 'w-full lg:pl-[100px] xl:px-[100px]': hasImageSizeL }"
                  class="mt-3 md:mt-4 mb-6 md:mb-8">
    <Image>
      <Figure>
        <Picture :class="isPortrait ? 'absolute top-0 flex justify-center items-center w-full aspect-4/3 md:aspect-16/9 z-10' : 'w-full aspect-4/3 md:aspect-16/9'">
          <template v-if="!image?.externalImageSrc">
            <source v-for="(singleSrc, index) in imgSrcSetArray(image as ContentBlocksAndAdSlotMarkers)"
                    :key="index + 'webp'"
                    type="image/webp"
                    :media="setMediaQuery(singleSrc.viewportWidth)"
                    :srcset="singleSrc.src">
          </template>
          <img :src="imgSrc()"
               :alt="image?.imageAltText"
               :title="image?.imageAltText"
               :class="isPortrait ? 'h-full max-h-full max-w-full object-contain' : 'w-full h-full object-cover'">
        </Picture>
        <PictureWithBlur v-if="isPortrait">
          <img :src="blurredBgSrc() ?? ''"
               alt=""
               class="w-full h-full object-cover filter blur-md scale-110">
        </PictureWithBlur>
        <Figcaption v-if="image?.imageCredit || image?.imageDescription?.html"
                    class="mt-2">
          <div class="text-ui-sm mb-2"
               v-html="toValidHTML(image?.imageDescription?.html ?? '')" />
          <p v-if="image?.imageCredit"
             class="text-ui-xs">
            © {{ image.imageCredit }}
          </p>
        </Figcaption>
      </Figure>
    </Image>
  </BlockContainer>
</template>
